import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import AdminModal from '../../components/admin/AdminModal';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';

const emptyProduct = {
  name: '',
  description: '',
  buttonLink: '',
  overlayImage: '',
  cost: 'Free',
  location: 'Twitch',
  tags: '',
  featured: false,
  buttonText: 'Download Free',
  sortOrder: 0,
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'featured', label: 'Featured' },
  { id: 'free', label: 'Free' },
  { id: 'premium', label: 'Premium' },
];

function isPremium(cost) {
  const value = (cost || '').toLowerCase();
  return value.includes('premium') || value.startsWith('$');
}

function toolPageSlug(link) {
  const match = /\/tools\/([^/?#]+)/.exec(link || '');
  return match ? match[1] : null;
}

export default function AdminTools() {
  const [products, setProducts] = useState([]);
  const [toolPageSlugs, setToolPageSlugs] = useState(new Set());
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = () => apiGet('/products').then(setProducts);

  useEffect(() => {
    load().catch(console.error);
    apiGet('/admin/tool-pages')
      .then((pages) => setToolPageSlugs(new Set(pages.map((p) => p.slug))))
      .catch(console.error);
  }, []);

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0) || a.name.localeCompare(b.name)),
    [products],
  );

  const stats = useMemo(() => {
    const premium = products.filter((p) => isPremium(p.cost)).length;
    return {
      total: products.length,
      featured: products.filter((p) => p.featured).length,
      free: products.length - premium,
      premium,
    };
  }, [products]);

  const query = search.trim().toLowerCase();
  const isFiltered = query !== '' || filter !== 'all';
  const visibleProducts = sortedProducts.filter((p) => {
    if (filter === 'featured' && !p.featured) return false;
    if (filter === 'free' && isPremium(p.cost)) return false;
    if (filter === 'premium' && !isPremium(p.cost)) return false;
    if (!query) return true;
    return [p.name, p.description, p.location, ...(p.tags || [])].join(' ').toLowerCase().includes(query);
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyProduct);
    setFormError('');
  };

  const startAdd = () => {
    const topOrder = sortedProducts.length ? (sortedProducts[0].sortOrder || 0) + 1 : 1;
    setEditingId(null);
    setForm({ ...emptyProduct, sortOrder: topOrder });
    setFormError('');
    setModalOpen(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      buttonLink: p.buttonLink,
      overlayImage: p.overlayImage,
      cost: p.cost,
      location: p.location,
      tags: (p.tags || []).join(', '),
      featured: p.featured,
      buttonText: p.buttonText,
      sortOrder: p.sortOrder || 0,
    });
    setFormError('');
    setModalOpen(true);
  };

  const startDuplicate = (p) => {
    startEdit(p);
    setEditingId(null);
    setForm((current) => ({ ...current, name: `${p.name} (copy)`, sortOrder: (p.sortOrder || 0) + 1 }));
  };

  const handleSave = async () => {
    setFormError('');
    setSaving(true);
    const payload = {
      ...form,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editingId) {
        await apiPut(`/products/${editingId}`, payload);
        setMessage(`Updated “${form.name}”.`);
      } else {
        await apiPost('/products', payload);
        setMessage(`Added “${form.name}” to the top of the catalog.`);
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete “${p.name}” from the catalog?`)) return;
    await apiDelete(`/products/${p.id}`);
    setMessage(`Deleted “${p.name}”.`);
    load();
  };

  const toggleFeatured = async (p) => {
    await apiPut(`/products/${p.id}`, { ...p, featured: !p.featured });
    load();
  };

  const persistOrder = async (next) => {
    const ordered = next.map((p, index) => ({ ...p, sortOrder: next.length - index }));
    setProducts(ordered);
    try {
      await Promise.all(
        ordered
          .filter((p) => sortedProducts.find((old) => old.id === p.id)?.sortOrder !== p.sortOrder)
          .map((p) => apiPut(`/products/${p.id}`, p)),
      );
      setMessage('Catalog order saved.');
    } catch (err) {
      setMessage(err.message);
      load();
    }
  };

  const linkedPage = toolPageSlug(form.buttonLink);

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Total</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--gold">
              <span className="admin-stat-value">{stats.featured}</span>
              <span className="admin-stat-label">Featured</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.free}</span>
              <span className="admin-stat-label">Free</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--purple">
              <span className="admin-stat-value">{stats.premium}</span>
              <span className="admin-stat-label">Premium</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-primary" onClick={startAdd}>
              <i className="fas fa-plus" aria-hidden="true" /> Add tool
            </button>
          </div>
        </div>
        <div className="admin-filter-row">
          <div className="admin-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tools, tags, platform…" />
          </div>
          <div className="admin-panel-tabs">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`admin-panel-tab ${filter === f.id ? 'active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="admin-filter-hint">
            {isFiltered ? `${visibleProducts.length} of ${products.length} shown · clear the filter to drag` : 'Drag rows to reorder · top shows first on the site'}
          </span>
        </div>
      </div>

      {message && <p className="admin-message">{message}</p>}

      <div className="admin-list-wrap">
        {visibleProducts.length === 0 ? (
          <div className="admin-list-empty">
            <p>{products.length ? 'No tools match this filter.' : 'No tools in the catalog yet.'}</p>
            {!products.length && <button type="button" className="btn btn-primary" onClick={startAdd}>Add first tool</button>}
          </div>
        ) : (
          <ul className="admin-list">
            {visibleProducts.map((p, index) => {
              const pageSlug = toolPageSlug(p.buttonLink);
              return (
                <SortableItem
                  as="li"
                  key={p.id}
                  listId="catalog-order"
                  index={index}
                  className="admin-list-item"
                  onMove={(from, to) => persistOrder(reorder(sortedProducts, from, to))}
                >
                  {!isFiltered && <DragHandle listId="catalog-order" index={index} />}
                  {p.overlayImage ? (
                    <img src={p.overlayImage} alt="" className="admin-thumb admin-thumb--wide" />
                  ) : (
                    <span className="admin-thumb admin-thumb--wide is-empty"><i className="fas fa-cube" aria-hidden="true" /></span>
                  )}
                  <div className="admin-list-main">
                    <div className="admin-list-title-row">
                      <span className="admin-list-title">{p.name}</span>
                    </div>
                    {p.description && <p className="admin-list-desc">{p.description}</p>}
                    <div className="admin-list-pills">
                      <span className={`admin-pill ${isPremium(p.cost) ? 'is-purple' : 'is-green'}`}>{p.cost || 'Free'}</span>
                      {p.location && <span className="admin-pill">{p.location}</span>}
                      {(p.tags || []).slice(0, 3).map((tag) => <span key={tag} className="admin-pill is-muted">{tag}</span>)}
                      {pageSlug && toolPageSlugs.has(pageSlug) && <span className="admin-pill is-muted"><i className="fas fa-file-alt" aria-hidden="true" /> Tool page</span>}
                    </div>
                  </div>
                  <div className="admin-list-actions">
                    <button
                      type="button"
                      className={`admin-icon-btn ${p.featured ? 'is-active' : ''}`}
                      onClick={() => toggleFeatured(p)}
                      title={p.featured ? 'Remove from featured' : 'Feature on home page'}
                    >
                      <i className={`${p.featured ? 'fas' : 'far'} fa-star`} aria-hidden="true" />
                    </button>
                    {p.buttonLink && (
                      <a href={p.buttonLink} target="_blank" rel="noopener noreferrer" className="admin-icon-btn" title="Open link">
                        <i className="fas fa-external-link-alt" aria-hidden="true" />
                      </a>
                    )}
                    <button type="button" className="admin-icon-btn" onClick={() => startDuplicate(p)} title="Duplicate">
                      <i className="far fa-copy" aria-hidden="true" />
                    </button>
                    <button type="button" className="admin-icon-btn" onClick={() => startEdit(p)} title="Edit">
                      <i className="fas fa-pen" aria-hidden="true" />
                    </button>
                    <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(p)} title="Delete">
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                </SortableItem>
              );
            })}
          </ul>
        )}
      </div>

      <AdminModal
        open={modalOpen}
        as="form"
        onSubmit={handleSave}
        onClose={closeModal}
        size="lg"
        title={editingId ? `Edit ${form.name || 'tool'}` : 'Add tool'}
        subtitle={editingId ? null : 'New tools go to the top of the catalog.'}
        footer={(
          <>
            {formError && <span className="admin-modal__error">{formError}</span>}
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add tool'}
            </button>
          </>
        )}
      >
        <div className="admin-modal-split">
          <div className="admin-modal-fields">
            <label className="admin-field">
              <span>Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
            </label>
            <label className="admin-field">
              <span>Description</span>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </label>
            <div className="admin-field-row">
              <label className="admin-field">
                <span>Cost</span>
                <input list="catalog-costs" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
              </label>
              <label className="admin-field">
                <span>Platform</span>
                <input list="catalog-locations" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </label>
            </div>
            <div className="admin-field-row">
              <label className="admin-field">
                <span>Button link</span>
                <input value={form.buttonLink} onChange={(e) => setForm({ ...form, buttonLink: e.target.value })} placeholder="https://zemaxi.com/tools/…" />
              </label>
              <label className="admin-field">
                <span>Button text</span>
                <input value={form.buttonText} onChange={(e) => setForm({ ...form, buttonText: e.target.value })} />
              </label>
            </div>
            {linkedPage && (
              <p className="admin-form-hint">
                {toolPageSlugs.has(linkedPage)
                  ? <>Links to the tool page <Link to="/admin/tool-pages">/tools/{linkedPage}</Link>.</>
                  : <>No tool page exists for /tools/{linkedPage} yet. Create one in <Link to="/admin/tool-pages">Tool Pages</Link> (AI can draft it).</>}
              </p>
            )}
            <label className="admin-field">
              <span>Tags</span>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Comma separated, e.g. OBS, Streamer.bot, Overlay" />
            </label>
            <label className="admin-switch">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <span>Featured on the home page</span>
            </label>
          </div>
          <aside className="admin-modal-side">
            <MediaPicker label="Card image" value={form.overlayImage} onChange={(url) => setForm({ ...form, overlayImage: url })} />
            <div className="admin-card-preview">
              {form.overlayImage ? <img src={form.overlayImage} alt="" /> : <div className="admin-card-preview__empty"><i className="fas fa-image" aria-hidden="true" /></div>}
              <div className="admin-card-preview__body">
                <span className={`admin-pill ${isPremium(form.cost) ? 'is-purple' : 'is-green'}`}>{form.cost || 'Free'}</span>
                <strong>{form.name || 'Tool name'}</strong>
                <p>{form.description || 'Description shows here.'}</p>
                <span className="admin-card-preview__btn">{form.buttonText || 'Button'}</span>
              </div>
            </div>
          </aside>
        </div>
        <datalist id="catalog-costs">
          {[...new Set(products.map((p) => p.cost).filter(Boolean))].map((v) => <option key={v} value={v} />)}
        </datalist>
        <datalist id="catalog-locations">
          {[...new Set(products.map((p) => p.location).filter(Boolean))].map((v) => <option key={v} value={v} />)}
        </datalist>
      </AdminModal>
    </div>
  );
}
